import { User, Session, AuthError } from '@supabase/supabase-js'
import { supabase } from '../config/supabase'

export interface LoginCredentials {
  email: string
  password: string
}

export interface SignUpCredentials extends LoginCredentials {
  confirmPassword?: string
}

// Actualiza la interface para incluir datos del perfil
export interface AuthResponse {
  user: User | null
  session: Session | null
  profile: UserProfile | null // Agregado
  error: AuthError | null
}

// AGREGADO: Interface para el perfil de usuario
export interface UserProfile {
  id: string
  email: string
  full_name?: string
  avatar_url?: string
  slug?: string
  // Rol opcional: si no existe en DB, se usa allowlist por email
  role?: 'user' | 'administrator'
  created_at: string
  updated_at: string
}

// AGREGADO: Interface para actualizar perfil
export interface UpdateProfileData {
  full_name?: string
  avatar_url?: string
  slug?: string
}

export class AuthService {
  private static instance: AuthService
  
  private constructor() {}
  
  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService()
    }
    return AuthService.instance
  }

  /**
   * Determina si el perfil corresponde a un administrador.
   * 1) Si el perfil trae role === 'administrator', es admin.
   * 2) Fallback: si el email está en VITE_ADMIN_EMAILS (separado por comas), es admin.
   */
  public static isAdministrator(profile: UserProfile | null | undefined): boolean {
    if (!profile) return false
    if ((profile as any).role === 'administrator') return true
    return false;
  }

  // Método para obtener usuario actual con perfil
  public async getCurrentUserWithProfile(): Promise<{ user: User | null; profile: UserProfile | null }> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      if (!user) {
        return { user: null, profile: null }
      }

      // Obtener perfil del usuario
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()

      if (error) {
        console.error('Error fetching user profile:', error)
        // Si no existe el perfil, crearlo
        const newProfile = await this.createProfile(user)
        return { user, profile: newProfile }
      }

      // Asegurar compatibilidad: si no existe role en DB, usar allowlist
      const withRole: UserProfile = {
        ...(profile as any),
        role: (profile as any)?.role ?? (AuthService.isAdministrator(profile as any) ? 'administrator' : 'user')
      }

      return { user, profile: withRole }
    } catch (error) {
      console.error('Error getting current user with profile:', error)
      return { user: null, profile: null }
    }
  }

  // Método para crear perfil si no existe
  private async createProfile(user: User): Promise<UserProfile | null> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .insert({
          id: user.id,
          email: user.email,
          full_name: user.user_metadata?.full_name || null,
          avatar_url: user.user_metadata?.avatar_url || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single()

      if (error) {
        console.error('Error creating profile:', error)
        return null
      }

      const withRole: UserProfile = {
        ...(data as any),
        role: AuthService.isAdministrator(data as any) ? 'administrator' : 'user'
      }
      return withRole
    } catch (error) {
      console.error('Error creating profile:', error)
      return null
    }
  }

  // Actualizar signIn para incluir perfil
  public async signIn(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      })
      
      let profile = null
      if (data.user) {
        const { profile: userProfile } = await this.getCurrentUserWithProfile()
        profile = userProfile
      }
      
      return {
        user: data.user,
        session: data.session,
        profile: profile,
        error: error,
      }
    } catch (error) {
      return {
        user: null,
        session: null,
        profile: null,
        error: error as AuthError,
      }
    }
  }

  // Actualizar signUp para CREAR perfil inmediatamente
  public async signUp(credentials: SignUpCredentials): Promise<AuthResponse> {
    try {
      const { data, error } = await supabase.auth.signUp({
        email: credentials.email,
        password: credentials.password,
      })

      let profile: UserProfile | null = null

      // Si hay usuario y sesión (no hay confirmación de email), crea el perfil ya
      if (!error && data.user && data.session) {
        profile = await this.createProfile(data.user)
        // Fallback: si falló el upsert, intenta leerlo
        if (!profile) {
          const { data: fetched } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', data.user.id)
            .single()
          profile = fetched ?? null
        }
      }

      // Si no hay sesión (confirmación de email activada), no podremos crear perfil aquí.
      // Se creará en el primer login vía getCurrentUserWithProfile().

      return {
        user: data.user,
        session: data.session,
        profile,
        error,
      }
    } catch (error) {
      return {
        user: null,
        session: null,
        profile: null,
        error: error as AuthError,
      }
    }
  }

  public async signOut(): Promise<{ error: AuthError | null }> {
    try {
      const { error } = await supabase.auth.signOut()
      return { error }
    } catch (error) {
      return { error: error as AuthError }
    }
  }

  public async signInWithGoogle(uri: string | undefined): Promise<AuthResponse> {
    try {
      console.log(uri ??  `${window.location.origin}/auth/callback`);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: uri ??  `${window.location.origin}/auth/callback`
        }
      })
      
      return {
        user: null, // OAuth redirect, user will be set on callback
        session: null,
        profile: null,
        error: error,
      }
    } catch (error) {
      return {
        user: null,
        session: null,
        profile: null,
        error: error as AuthError,
      }
    }
  }

  public async getCurrentUser(): Promise<User | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      return user
    } catch (error) {
      console.error('Error getting current user:', error)
      return null
    }
  }

  public async getCurrentSession(): Promise<Session | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      return session
    } catch (error) {
      console.error('Error getting current session:', error)
      return null
    }
  }

  public async resetPassword(email: string): Promise<{ error: AuthError | null }> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      })
      return { error }
    } catch (error) {
      return { error: error as AuthError }
    }
  }

  // AGREGADO: Método necesario para UserProfilePage
  public async getUserById(userId: string): Promise<{ data: UserProfile | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Nuevo: obtener usuario por slug (sin columna slug en DB). Resuelve comparando slugify(full_name) o parte local del email
  public async getUserBySlug(slug: string): Promise<{ data: UserProfile | null; error: any }> {
    try {
      const { slugify } = await import('../utils/slugify');
      const decoded = decodeURIComponent(slug || '');
      const normalized = slugify(decoded);

      // 1) Buscar candidatos usando ilike con palabras separadas (con acentos tal cual)
      const words = decoded.replace(/-/g, ' ').trim();
      let { data: candidates, error: queryError } = await supabase
        .from('profiles')
        .select('id, email, full_name, avatar_url, created_at, updated_at')
        .or(`full_name.ilike.%${words}%,email.ilike.%${words}%`);

      if (queryError) {
        return { data: null, error: queryError };
      }

      // 2) Fallback: si no hay candidatos, traer un set pequeño y filtrar por slugify (ignora acentos)
      if (!candidates || candidates.length === 0) {
        const { data: all, error: allErr } = await supabase
          .from('profiles')
          .select('id, email, full_name, avatar_url, created_at, updated_at')
          .limit(2000); // límite de seguridad
        if (allErr) return { data: null, error: allErr };
        candidates = all || [];
      }

      // 3) Match exacto por slugify sobre nombre completo o parte local del email
      const exact = (candidates || []).find((p: any) => {
        const base = (p.full_name || (p.email ? p.email.split('@')[0] : '')) as string;
        return slugify(base) === normalized;
      });

      return { data: (exact as any) || null, error: exact ? null : { message: 'User not found' } };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Nuevo: resolver por slug o UUID
  public async getUserBySlugOrId(identifier: string): Promise<{ data: UserProfile | null; error: any; resolvedBy: 'slug' | 'id' | null }> {
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(identifier);
    if (isUuid) {
      const byId = await this.getUserById(identifier);
      return { ...byId, resolvedBy: 'id' };
    }
    const bySlug = await this.getUserBySlug(identifier);
    return { ...bySlug, resolvedBy: 'slug' };
  }

  // AGREGADO: Método para actualizar perfil
  public async updateProfile(userId: string, profileData: UpdateProfileData): Promise<{ data: UserProfile | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({
          ...profileData,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select()
        .single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  // AGREGADO: Método para subir avatar
  public async uploadAvatar(userId: string, file: File): Promise<{ data: string | null; error: any }> {
    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${userId}.${fileExt}`
      const filePath = `${fileName}`
      

      // Subir archivo
      const { error: uploadError } = await supabase.storage
        .from('user-avatars')
        .upload(filePath, file, { upsert: true })

      if (uploadError) {
        return { data: null, error: uploadError }
      }

      // Obtener URL pública
      const { data: { publicUrl } } = supabase.storage
        .from('user-avatars')
        .getPublicUrl(filePath)

      return { data: publicUrl, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  public onAuthStateChange(callback: (event: string, session: Session | null) => void) {
    return supabase.auth.onAuthStateChange(callback)
  }

  // Nuevo: comprobar disponibilidad de slug sin columna slug. Compara slugify(full_name) o parte local del email
  public async isUserSlugAvailable(slug: string, excludeUserId?: string): Promise<{ available: boolean; error: any }> {
    try {
      const { slugify } = await import('../utils/slugify');
      const decoded = decodeURIComponent(slug || '');
      const normalized = slugify(decoded);

      const words = decoded.replace(/-/g, ' ').trim();
      let { data: candidates, error } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .or(`full_name.ilike.%${words}%,email.ilike.%${words}%`);
      if (error) return { available: false, error };

      if (!candidates || candidates.length === 0) {
        const { data: all, error: allErr } = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .limit(2000);
        if (allErr) return { available: false, error: allErr };
        candidates = all || [];
      }

      const conflict = (candidates || []).some((p: any) => {
        const base = (p.full_name || (p.email ? p.email.split('@')[0] : '')) as string;
        return slugify(base) === normalized && p.id !== excludeUserId;
      });
      return { available: !conflict, error: null };
    } catch (error) {
      return { available: false, error };
    }
  }
}